import {
  Page,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Banner,
  Spinner,
  EmptyState,
} from '@shopify/polaris';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/AuthProvider';
import {
  usePublishRequests,
  useApproveRequest,
  useRejectRequest,
  type PublishRequest,
} from '@/queries/users';
import { ApiError } from '@/types/api';

function when(ts: number): string {
  if (!ts) return '';
  return new Date(ts * 1000).toLocaleString();
}

export function ApprovalsPage() {
  const navigate = useNavigate();
  const { admin } = useAuth();
  const isMain = Boolean(admin?.is_main);

  const { data, isLoading, error } = usePublishRequests(isMain);
  const approve = useApproveRequest();
  const reject = useRejectRequest();

  if (!isMain) {
    return (
      <Page
        title="Publish approvals"
        backAction={{ content: 'Editor', onAction: () => navigate('/') }}
      >
        <Banner tone="critical" title="Main account only">
          <p>Only the main account can review publish requests.</p>
        </Banner>
      </Page>
    );
  }

  const requests = data?.requests ?? [];

  return (
    <Page
      title="Publish approvals"
      backAction={{ content: 'Editor', onAction: () => navigate('/') }}
      subtitle="Publishes from other users wait here for your approval."
    >
      <BlockStack gap="400">
        {error ? (
          <Banner tone="critical" title="Could not load requests">
            <p>{error instanceof ApiError ? error.message : 'Unknown error'}</p>
          </Banner>
        ) : null}

        {isLoading ? (
          <Card>
            <InlineStack align="center"><Spinner size="small" /></InlineStack>
          </Card>
        ) : requests.length === 0 ? (
          <Card>
            <EmptyState
              heading="No pending publishes"
              image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
            >
              <p>When a user clicks Publish, their request appears here.</p>
            </EmptyState>
          </Card>
        ) : (
          requests.map((r: PublishRequest) => (
            <Card key={r.id}>
              <InlineStack align="space-between" blockAlign="center" gap="400">
                <BlockStack gap="100">
                  <Text as="p" variant="headingSm">
                    {r.requested_by_email}
                  </Text>
                  <Text as="p" tone="subdued" variant="bodySm">
                    Template: {r.template} · Requested {when(r.requested_at)}
                  </Text>
                </BlockStack>
                <InlineStack gap="200">
                  <Button
                    tone="critical"
                    variant="plain"
                    loading={reject.isPending}
                    onClick={() => {
                      if (window.confirm('Reject this publish?'))
                        reject.mutate({ id: r.id });
                    }}
                  >
                    Reject
                  </Button>
                  <Button
                    variant="primary"
                    loading={approve.isPending}
                    onClick={() => approve.mutate(r.id)}
                  >
                    Approve &amp; publish
                  </Button>
                </InlineStack>
              </InlineStack>
            </Card>
          ))
        )}
      </BlockStack>
    </Page>
  );
}
